import {
  CompleteMultipartUploadCommand,
  CreateMultipartUploadCommand,
  PutObjectCommand,
  UploadPartCommand,
} from '@aws-sdk/client-s3'
import { API_BASE_URL } from './utils/apiConfig'

let candidateSession: { assignmentId: string; candidateToken: string } | null = null

export function setVideoUploadSession(assignmentId: string, candidateToken: string) {
  candidateSession = { assignmentId, candidateToken }
}

async function requestUpload(path: string, payload: Record<string, unknown>) {
  if (!candidateSession) throw new Error('Candidate session required for video upload')
  const response = await fetch(`${API_BASE_URL}/api/video-uploads/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...payload, ...candidateSession }),
    signal: AbortSignal.timeout(120000),
  })
  if (!response.ok) throw new Error(`Video upload request failed: ${response.status}`)
  return response.json()
}

async function putToSignedUrl(url: string, body: Uint8Array, contentType?: string) {
  const response = await fetch(url, {
    method: 'PUT',
    headers: contentType ? { 'Content-Type': contentType } : undefined,
    body,
    signal: AbortSignal.timeout(120000),
  })
  if (!response.ok) throw new Error(`Video upload failed: ${response.status}`)
  return response
}

export const s3 = {
  async send(command: unknown): Promise<any> {
    if (command instanceof CreateMultipartUploadCommand) {
      const { Bucket, Key } = command.input
      const result = await requestUpload('start', { bucket: Bucket, key: Key })
      return { UploadId: result.uploadId }
    }
    if (command instanceof UploadPartCommand) {
      const { Bucket, Key, UploadId, PartNumber, Body } = command.input
      const { url } = await requestUpload('sign-part', {
        bucket: Bucket, key: Key, uploadId: UploadId, partNumber: PartNumber,
      })
      const response = await putToSignedUrl(url, Body as Uint8Array)
      return { ETag: response.headers.get('ETag') }
    }
    if (command instanceof PutObjectCommand) {
      const { Bucket, Key, Body, ContentType } = command.input
      const { url } = await requestUpload('sign-object', { bucket: Bucket, key: Key })
      await putToSignedUrl(url, Body as Uint8Array, ContentType)
      return {}
    }
    if (command instanceof CompleteMultipartUploadCommand) {
      if (!candidateSession) throw new Error('Candidate session required for video upload')
      const { Bucket, Key, UploadId, MultipartUpload } = command.input
      const response = await fetch(`${API_BASE_URL}/api/complete-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bucket: Bucket,
          key: Key,
          uploadId: UploadId,
          employeeTestId: candidateSession.assignmentId,
          ...candidateSession,
          chunkInfo: MultipartUpload?.Parts?.map((part) => ({
            partNumber: part.PartNumber,
            etag: part.ETag,
          })),
        }),
        signal: AbortSignal.timeout(120000),
      })
      if (!response.ok) throw new Error(`Video completion failed: ${response.status}`)
      const result = await response.json()
      if (result.status !== 'completed') throw new Error('Video completion failed')
      return { Key: result.key }
    }
    throw new Error('Unsupported video upload command')
  },
}
